import { memo } from 'react';
import { useReduxState } from 'hooks';
import { TimeIndicator } from 'components/common';

import type { EditorHeaderStateProps } from './EditorHeader';

type Props = Pick<
  EditorHeaderStateProps,
  'status' | 'updatedAt' | 'isDraft' | 'previewType'
>;

const EditorHeaderStatus = ({
  status,
  updatedAt,
  isDraft,
  previewType
}: Props) => {
  const { language, messages, theme } = useReduxState();

  const statusOptions = previewType
    ? null
    : {
        connectionError: {
          class: 'error',
          content: messages.editorConnectionError,
          icon: 'exclamation-triangle'
        },
        error: {
          class: 'error',
          content: messages.commonError,
          icon: 'exclamation-triangle'
        },
        importError: {
          class: 'error',
          content: messages.editorImportError,
          icon: 'exclamation-triangle'
        },
        importSuccess: {
          class: 'success',
          content: messages.editorImportSuccess,
          icon: 'check'
        },
        importWarning: {
          class: 'warning',
          content: messages.editorImportWarning,
          icon: 'exclamation-triangle'
        },
        saved: {
          class: isDraft ? 'warning' : 'success',
          content: (
            <span>
              {`${
                isDraft ? messages.commonSavedAsDraft : messages.commonSaved
              } `}
              {updatedAt ? (
                <TimeIndicator
                  language={language}
                  style={{ textTransform: 'lowercase' }}
                  timeString={updatedAt}
                />
              ) : null}
            </span>
          ),
          icon: isDraft ? 'pen' : 'check'
        },
        saving: {
          class: 'default',
          content: messages.commonSaving,
          icon: ['far', 'clock']
        }
      };

  const data = previewType
    ? {
        class: '',
        content: messages.coursePreviewingVersionEditingDisabled
      }
    : statusOptions[status || 'saved'];

  return (
    <div className={`status status-${data.class}`}>
      <div className="content">{data.content}</div>

      <style jsx>{`
        .status {
          color: ${theme.textColorLight};
          font-size: 1.3rem;
          font-weight: 400;
        }
        .status-error {
          color: ${theme.dangerColor};
        }
        .status-success {
          color: ${theme.successStandaloneColor};
        }
        .status-warning {
          color: ${theme.warningStandaloneColor};
        }
        .content {
          display: inline-block;
        }
      `}</style>
    </div>
  );
};

export default memo(EditorHeaderStatus);
